import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RiskLevelEnum } from '../enums/task.enums';
import { TaskService } from './task.service';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';

export interface RiskAlert {
  walletId: string;
  walletAddress: string;
  walletName: string;
  blockchain: string;
  previousRiskLevel: RiskLevelEnum;
  currentRiskLevel: RiskLevelEnum;
  source: string;
  reportUrl?: string;
  alertTimestamp: Date;
  details?: any;
}

export interface WalletRiskAssessment {
  walletId: string;
  walletAddress: string;
  currentRiskLevel: RiskLevelEnum;
  lastAssessmentDate: Date;
  source: string;
  details?: any;
}

@Injectable({
  providedIn: 'root',
})
export class RiskMonitoringService {
  private http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);

  private apiUrl = `${this.cfg.baseUrl}/api/RiskMonitoring`;
  private taskService = inject(TaskService);

  /**
   * Get current risk assessment for a wallet
   */
  getWalletRiskAssessment(walletId: string): Observable<WalletRiskAssessment> {
    return this.http.get<WalletRiskAssessment>(`${this.apiUrl}/wallet/${walletId}/risk`);
  }

  /**
   * Get all risk alerts for a wallet
   */
  getWalletRiskAlerts(walletId: string): Observable<RiskAlert[]> {
    return this.http.get<RiskAlert[]>(`${this.apiUrl}/wallet/${walletId}/alerts`);
  }

  /**
   * Update wallet risk level (manual override)
   */
  updateWalletRiskLevel(walletId: string, newRiskLevel: RiskLevelEnum, reason: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/wallet/${walletId}/risk-level`, {
      riskLevel: newRiskLevel,
      reason,
      source: 'Manual Override',
    });
  }

  /**
   * Disable wallet due to risk
   */
  disableWalletForRisk(walletId: string, reason: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/wallet/${walletId}/disable`, {
      reason,
      disabledBy: 'Compliance Team',
    });
  }

  /**
   * Log risk alert without creating a task
   */
  private logRiskAlert(alert: RiskAlert): Observable<any> {
    return this.http.post(`${this.apiUrl}/alerts/log`, alert);
  }
}
